import { useMemo } from 'react';
import { useSiteLocations } from '@hooks/useSiteLocations';
import { useActivitiesFromSchedule } from '../hooks/useActivitiesFromSchedule';
import type { IListItem } from '../model/listItem';
import { buildProgressMatrix, computeTaskDepth, type ColumnSort, type IProgressMatrix, type SortDir } from './progressMatrix';
import { hasAreas } from './trackerPopulation';

/**
 * Progress Tracker view model. Everything is REAL: the columns are `shown`,
 * the tracker's activities the PAGE chose (`useTrackerPopulation` — the same
 * selector, filters and date window the Daily List keeps its activities by);
 * each task's per-area `locations` (area + status) ride along on the schedule
 * fetch, and the floor→zone catalog is the real Trusstor area catalog
 * (`useSiteLocations`) the Scheduler's Locations tab assigns against.
 */
export function useProgressMatrix(
  siteId: string,
  generation: number,
  sort: ColumnSort,
  dir: SortDir,
  shown: readonly IListItem[],
): { matrix: IProgressMatrix; scheduleNumber: string | undefined; taskGenerations: Map<string, number> } {
  // The whole feed — the parentId chains a column's level reads.
  const { tasks, groups, scheduleNumber } = useActivitiesFromSchedule(siteId);
  const locations = useSiteLocations(siteId);
  const matrix = useMemo(
    () => buildProgressMatrix({ tasks, shown, groups, locations, generation, sort, dir }),
    [tasks, shown, groups, locations, generation, sort, dir],
  );
  // Which generation each LOCATED task surfaces at (depth 0 = generation 2;
  // generation 1 is the groups level) — the dependency jump uses this to
  // switch levels when the target isn't a column at the current one. Ignores
  // the filters deliberately: they don't change which generation a task
  // belongs to.
  const taskGenerations = useMemo(() => {
    const depth = computeTaskDepth(tasks);
    const generations = new Map<string, number>();
    for (const task of tasks) {
      if (hasAreas(task)) generations.set(task.id, (depth.get(task.id) ?? 0) + 2);
    }
    return generations;
  }, [tasks]);
  return { matrix, scheduleNumber, taskGenerations };
}
